import { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import type { CompletedOnboardingRow, OnboardingCounts, OnboardingListRow } from "../../types/onboarding";

interface Loaded<T> {
  data: T | null;
  error: string | null;
}

const NOT_LOADED = { data: null, error: null };

// Each list is null until its answer arrives. The completed list is fetched only while it is shown.
export function useOnboardingDashboard(showCompleted: boolean) {
  const { get } = useApi();
  // Both lists are GETs to /api/onboardings, and a GET supersedes one in flight to the same path on its instance.
  const { get: getCompleted } = useApi();
  const [open, setOpen] = useState<Loaded<OnboardingListRow[]>>(NOT_LOADED);
  const [counts, setCounts] = useState<Loaded<OnboardingCounts>>(NOT_LOADED);
  const [completed, setCompleted] = useState<Loaded<CompletedOnboardingRow[]>>(NOT_LOADED);

  useEffect(() => {
    let current = true;
    setOpen(NOT_LOADED);
    get<OnboardingListRow[]>("/api/onboardings?status=open")
      .then((data) => { if (current) setOpen({ data, error: null }); })
      .catch((err: Error) => { if (current) setOpen({ data: null, error: err.message }); });
    return () => { current = false; };
  }, [get]);

  useEffect(() => {
    let current = true;
    setCounts(NOT_LOADED);
    get<OnboardingCounts>("/api/onboardings/counts")
      .then((data) => { if (current) setCounts({ data, error: null }); })
      .catch((err: Error) => { if (current) setCounts({ data: null, error: err.message }); });
    return () => { current = false; };
  }, [get]);

  useEffect(() => {
    let current = true;
    setCompleted(NOT_LOADED);
    if (showCompleted) {
      getCompleted<CompletedOnboardingRow[]>("/api/onboardings?status=complete")
        .then((data) => { if (current) setCompleted({ data, error: null }); })
        .catch((err: Error) => { if (current) setCompleted({ data: null, error: err.message }); });
    }
    return () => { current = false; };
  }, [getCompleted, showCompleted]);

  return { open, counts, completed };
}
